/**
 * Best-effort publisher-page context detection (Phase 2).
 *
 * The runtime is a cross-origin iframe, so most page context is only
 * partially observable. Every signal here degrades gracefully: a field that
 * can't be resolved is OMITTED from the returned object (never set to
 * undefined, never thrown). The transport spreads whatever resolved into the
 * stream `allBase`.
 *
 * Deliberately NOT collected (server-side concern per EM):
 *   - ipAddress, region
 *
 * `country` is APPROXIMATE — inferred from timezone/locale with no network
 * call and no permission prompt (no Geolocation API). It is a best-effort
 * hint, not authoritative geo.
 *
 * The pure resolvers (resolvePageLocation / resolveUtm / resolveCountry) take
 * their inputs explicitly so they're unit-testable without stubbing globals;
 * detectPageContext() reads the globals and delegates.
 */

import type { DeviceType } from '@/analytics/types';

export interface PageContext {
  url?: string;
  domain?: string;
  canonical?: string;
  country?: string;
  utm?: Record<string, string>;
}

// Pragmatic, intentionally-small IANA-timezone → ISO-3166-alpha2 map. Covers
// the common publisher markets (Brazil-heavy per the product's pt-BR focus).
// Unknown timezones fall through to the locale region, then to omission —
// we never guess wildly.
const TZ_COUNTRY: Record<string, string> = {
  'America/Sao_Paulo': 'BR',
  'America/Bahia': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Manaus': 'BR',
  'America/Belem': 'BR',
  'America/Cuiaba': 'BR',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Mexico_City': 'MX',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Santiago': 'CL',
  'Europe/London': 'GB',
  'Europe/Lisbon': 'PT',
  'Europe/Madrid': 'ES',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Warsaw': 'PL',
  'Europe/Moscow': 'RU',
  'Asia/Kolkata': 'IN',
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Asia/Singapore': 'SG',
  'Asia/Dubai': 'AE',
  'Australia/Sydney': 'AU',
  'Africa/Johannesburg': 'ZA',
  'Africa/Lagos': 'NG',
};

function safeUrl(value: string): URL | undefined {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

/**
 * True when `referrer` points at the runtime's own page (same origin AND
 * pathname, ignoring query/hash). A self-referral happens on a direct load
 * that was reached by reloading or self-navigating quiz.html — document.referrer
 * then holds the iframe's own URL, which must never be reported as the
 * publisher page. A genuine same-origin parent (different pathname, e.g. a
 * local parent.html embedding quiz.html) is NOT a self-referral.
 */
export function isSelfReferral(referrer: string, selfHref: string): boolean {
  const ref = safeUrl(referrer);
  const self = safeUrl(selfHref);
  if (!ref || !self) return false;
  return ref.origin === self.origin && ref.pathname === self.pathname;
}

/**
 * Resolve url/domain/canonical from the best available signal.
 *   - referrer (the parent page URL at iframe load; cross-origin-readable but
 *     empty under a strict referrer policy) → full url + domain + canonical
 *     (canonical = origin+pathname, query/hash stripped).
 *   - else ancestorOrigins[0] (Chromium/Safari; absent in Firefox) → domain
 *     only (an origin carries no path).
 *   - else nothing.
 *
 * The runtime's OWN window.location is NEVER used as a fallback — the iframe's
 * own URL is not the publisher page. The caller passes a referrer that has
 * already been self-referral-filtered (see detectPageContext); on a direct
 * load with no parent this is empty and url/domain/canonical are omitted.
 */
export function resolvePageLocation(
  referrer: string,
  ancestorOrigin: string | undefined,
): { url?: string; domain?: string; canonical?: string } {
  if (referrer) {
    const parsed = safeUrl(referrer);
    if (parsed) {
      return {
        url: referrer,
        domain: parsed.hostname,
        canonical: `${parsed.origin}${parsed.pathname}`,
      };
    }
  }
  if (ancestorOrigin) {
    const parsed = safeUrl(ancestorOrigin);
    if (parsed) {
      return { domain: parsed.hostname };
    }
  }
  return {};
}

/** Parse utm_* params out of the referrer query string. Omitted when the
 *  referrer is empty/unparseable or carries no utm params. */
export function resolveUtm(referrer: string): Record<string, string> | undefined {
  if (!referrer) return undefined;
  const parsed = safeUrl(referrer);
  if (!parsed) return undefined;
  const utm: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    if (value && key.toLowerCase().startsWith('utm_')) utm[key] = value;
  });
  return Object.keys(utm).length > 0 ? utm : undefined;
}

/**
 * Approximate country. Primary signal: IANA timezone → country. Fallback:
 * the region subtag of the locale (e.g. `pt-BR` → `BR`). Omitted when neither
 * resolves. No Geolocation API (would prompt) and no network call.
 */
export function resolveCountry(
  timeZone: string | undefined,
  language: string | undefined,
): string | undefined {
  if (timeZone) {
    const byTz = TZ_COUNTRY[timeZone];
    if (byTz) return byTz;
  }
  if (language) {
    // BCP-47 region subtag: exactly two letters not followed by another
    // letter (skips 4-letter script subtags like `Hans`).
    const match = /-([A-Za-z]{2})(?![A-Za-z])/.exec(language);
    if (match?.[1]) return match[1].toUpperCase();
  }
  return undefined;
}

/**
 * Device class from the iframe's own UA. Lives here (not in the engine) so
 * the engine's `base.device` and any page-context consumer share one detector.
 */
export function detectDevice(): DeviceType {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  // Tablets first: iPad / generic tablet markers, and Android WITHOUT the
  // "Mobile" token (Android phones include "Mobile", tablets don't).
  if (/\b(iPad|Tablet|PlayBook|Silk)\b/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    return 'tablet';
  }
  if (/Mobi|iPhone|iPod|Android|IEMobile|BlackBerry|Opera Mini/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

function readReferrer(): string {
  try {
    return document.referrer || '';
  } catch {
    return '';
  }
}

function readSelfHref(): string {
  try {
    return window.location.href || '';
  } catch {
    return '';
  }
}

function readAncestorOrigin(): string | undefined {
  try {
    const origins = window.location.ancestorOrigins;
    if (origins && origins.length > 0) {
      const first = origins[0];
      // Sandboxed/opaque ancestors report "null" — treat as absent.
      if (first && first !== 'null') return first;
    }
  } catch {
    // Firefox: ancestorOrigins is undefined → omit.
  }
  return undefined;
}

function readTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function readLanguage(): string | undefined {
  try {
    return navigator.language || undefined;
  } catch {
    return undefined;
  }
}

/** Read all available globals and assemble the page context, omitting every
 *  field that couldn't be resolved. */
export function detectPageContext(): PageContext {
  const rawReferrer = readReferrer();
  // Drop a self-referral so the iframe's own URL never masquerades as the
  // publisher page. On a direct load (no parent) referrer is empty anyway;
  // this also covers a reload/self-navigation where it holds quiz.html's URL.
  const referrer = isSelfReferral(rawReferrer, readSelfHref()) ? '' : rawReferrer;

  const loc = resolvePageLocation(referrer, readAncestorOrigin());
  const utm = resolveUtm(referrer);
  const country = resolveCountry(readTimeZone(), readLanguage());

  const ctx: PageContext = {};
  if (loc.url !== undefined) ctx.url = loc.url;
  if (loc.domain !== undefined) ctx.domain = loc.domain;
  if (loc.canonical !== undefined) ctx.canonical = loc.canonical;
  if (country !== undefined) ctx.country = country;
  if (utm !== undefined) ctx.utm = utm;
  return ctx;
}
